import { useNavigate } from 'react-router-dom'
import { Button } from '@/shared/ui'
import { useAccount } from '@/entities/session'
import { useProfile } from '@/entities/profile'
import { useWorkingHours, summarize } from '@/entities/working-hours'
import { useCalls } from '@/entities/call'
import { timezoneLabel } from '@/shared/config/timezones'
import styles from './SetupMap.module.css'

interface Step {
  name: string
  /** Что уже заполнено. Пусто — шаг не пройден. */
  state: string
  done: boolean
  path: string
}

/**
 * Карта настройки агента.
 *
 * Показывает не «сколько процентов заполнено», а что именно сделано и
 * куда идти дальше. Проценты сами по себе не говорят ничего: человек,
 * увидев 60%, всё равно идёт искать, чего не хватает.
 */
export function SetupMap() {
  const navigate = useNavigate()
  const { data: account } = useAccount()
  const profile = useProfile()
  const hours = useWorkingHours()
  const calls = useCalls({})

  const steps: Step[] = [
    {
      name: 'Компания',
      state: account ? timezoneLabel(account.company.timezone) : '',
      done: Boolean(account?.company.name),
      path: '/settings/company',
    },
    {
      name: 'Чем занимаетесь',
      state: profile.data?.business.trim() ? 'заполнено' : '',
      done: Boolean(profile.data?.business.trim()),
      path: '/settings/profile',
    },
    {
      name: 'Что предлагаете',
      state: profile.data?.offerings.trim() ? 'заполнено' : '',
      done: Boolean(profile.data?.offerings.trim()),
      path: '/settings/profile',
    },
    {
      name: 'Цель звонка',
      state: profile.data?.goal.trim() ? 'задана' : '',
      done: Boolean(profile.data?.goal.trim()),
      path: '/settings/profile',
    },
    {
      name: 'Часы звонков',
      state: hours.data ? summarize(hours.data) : '',
      done: Boolean(hours.data?.some((day) => !day.closed)),
      path: '/settings/hours',
    },
    {
      name: 'Голос агента',
      state: 'настроен по умолчанию',
      done: true,
      path: '/settings/voice',
    },
    {
      name: 'Проверка звонком',
      state: calls.data?.length ? `${calls.data.length} ${plural(calls.data.length)}` : '',
      done: Boolean(calls.data?.length),
      path: '/test-call',
    },
  ]

  const done = steps.filter((step) => step.done).length
  const percent = Math.round((done / steps.length) * 100)
  // Первый незакрытый шаг — тот, за который браться следующим.
  const current = steps.findIndex((step) => !step.done)

  return (
    <div className={styles.map}>
      <div className={styles.head}>
        <span className={styles.title}>Карта настройки</span>
        <span className={styles.counter}>
          {done} из {steps.length} · {percent}%
        </span>
      </div>

      <div className={styles.progress}>
        <div
          className={`${styles.progressFill} ${percent === 100 ? styles.progressDone : ''}`}
          style={{ width: `${percent}%` }}
        />
      </div>

      <div className={styles.steps}>
        {steps.map((step, i) => (
          <button
            key={step.name}
            type="button"
            className={styles.step}
            onClick={() => navigate(step.path)}
          >
            <span
              className={[
                styles.badge,
                step.done ? styles.badgeDone : '',
                i === current ? styles.badgeCurrent : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {step.done ? <CheckIcon /> : i + 1}
            </span>

            <span className={styles.body}>
              <span className={styles.name}>{step.name}</span>
              <span className={`${styles.state} ${step.done ? styles.stateDone : ''}`}>
                {step.state || (i === current ? 'следующий шаг' : 'пока не заполнено')}
              </span>
            </span>
          </button>
        ))}
      </div>

      <div className={styles.footer}>
        <p className={styles.footerNote}>
          {percent === 100
            ? 'Всё заполнено. Позвоните агенту ещё раз, если правили анкету — услышите, что изменилось.'
            : 'Агент звонит и с неполной анкетой, но чем меньше в ней сказано, тем больше он выдумывает на ходу.'}
        </p>
        <Button size="sm" fullWidth onClick={() => navigate('/test-call')}>
          Позвонить агенту
        </Button>
      </div>
    </div>
  )
}

function plural(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'звонок'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'звонка'
  return 'звонков'
}

function CheckIcon() {
  return (
    <svg width="9" height="7" viewBox="0 0 9 7" fill="none" aria-hidden="true">
      <path
        d="M1 3.6L3.3 5.8L8 1"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
